import React from 'react'
import TextField from '@mui/material/TextField'
import Autocomplete, { autocompleteClasses } from '@mui/material/Autocomplete'
import Paper from '@mui/material/Paper'
import { ListChildComponentProps, VariableSizeList } from 'react-window'
import ListSubheader from '@mui/material/ListSubheader'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import Popper from '@mui/material/Popper'
import { styled } from '@mui/styles'
import { useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { OutOfStockStatusEnum } from '../../../../generated/gqlTypes'
import { ProductVariantPropertyJson } from '../types'

const LISTBOX_PADDING = 13
const INITIAL_VAL = '--- Click to select ---'

function renderRow(props: ListChildComponentProps) {
  const { data, index, style } = props
  const dataSet = data[index]
  const inlineStyle = {
    ...style,
    top: (style.top as number) + LISTBOX_PADDING,
  }

  if (dataSet.hasOwnProperty('group')) {
    return (
      <ListSubheader key={dataSet.key} component='div' style={inlineStyle}>
        {dataSet.group}
      </ListSubheader>
    )
  }
  return (
    <Typography component='li' {...dataSet.props} noWrap style={inlineStyle}>
      {dataSet.key}
    </Typography>
  )
}

function useResetCache(data: any) {
  const ref = React.useRef<VariableSizeList>(null)
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true)
    }
  }, [data])
  return ref
}

const OuterElementContext = React.createContext({})

const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = React.useContext(OuterElementContext)
  return <div ref={ref} {...props} {...outerProps} />
})

// Adapter for react-window
const ListboxComponent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLElement>>(function ListboxComponent(
  props,
  ref
) {
  const { children, ...other } = props
  const itemData: React.ReactChild[] = []
  ;(children as React.ReactChild[]).forEach((item: React.ReactChild & { children?: React.ReactChild[] }) => {
    itemData.push(item)
    itemData.push(...(item.children || []))
  })

  const theme = useTheme()
  const smUp = useMediaQuery(theme.breakpoints.up('sm'), {
    noSsr: true,
  })
  const itemCount = itemData.length
  const itemSize = smUp ? 36 : 48

  const getChildSize = (child: React.ReactChild) => {
    if (child.hasOwnProperty('group')) {
      return 48
    }

    return itemSize
  }

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize
    }
    return itemData.map(getChildSize).reduce((a, b) => a + b, 0)
  }

  const gridRef = useResetCache(itemCount)

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width='100%'
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType='ul'
          itemSize={index => getChildSize(itemData[index])}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  )
})

const StyledPopper = styled(Popper)({
  [`& .${autocompleteClasses.listbox}`]: {
    boxSizing: 'border-box',
    '& ul': {
      padding: 0,
      margin: 0,
    },
  },
})

interface Props {
  options: string[]
  label: string
  selected: string
  currentProps: ProductVariantPropertyJson[][]
  allProps: ProductVariantPropertyJson[][]

  onChange(label: string, option: string): void
}

const VariantPickerWidget: React.FC<Props> = ({ options, label, selected, onChange, currentProps, allProps }) => {
  const { t } = useTranslation()

  const isInitial = selected === INITIAL_VAL || (!selected && options.length > 1)

  const initialSelected = options.length > 1 ? INITIAL_VAL : options[0]
  return (
    <div className='mb-20'>
      <label className='font-medium text-15 mb-8' htmlFor={label}>
        {label}
      </label>
      <Autocomplete
        fullWidth
        disableClearable
        autoComplete
        options={options.length > 1 ? [INITIAL_VAL, ...options] : options}
        value={selected || initialSelected}
        onChange={(e, value: string) => {
          onChange(label, value)
        }}
        getOptionLabel={option => {
          if (option === INITIAL_VAL) {
            return t('formOptions.clickToSelect', '--- Click to select ---')
          }
          if (currentProps.length) {
            const props = currentProps.find(props_ => props_.find(prop_ => prop_.valueName === option))

            if (props?.length) {
              const prop = props[0]
              let stockStatus = ''
              if (prop.stockStatus === OutOfStockStatusEnum.In) {
                stockStatus = ''
              } else if (prop.stockStatus === OutOfStockStatusEnum.Backorder) {
                stockStatus = t('productBackorder', 'Backorder')
              } else if (prop.stockStatus === OutOfStockStatusEnum.Out) {
                stockStatus = t('productOutOfStock', 'Out of Stock')
              }
              return stockStatus ? `${option} (${stockStatus})` : option
            }
          }

          if (allProps.length) {
            const props = allProps.filter(props_ => props_.find(prop_ => prop_.valueName === option))
            const isOutOfStock = props.flat().every(prop_ => prop_.stockStatus === OutOfStockStatusEnum.Out)
            if (isOutOfStock) {
              const stockStatus = t('productOutOfStock', 'Out of Stock')
              return `${option} (${stockStatus})`
            }
            const isBackorder = props
              .flat()
              .every(prop_ => prop_.stockStatus === OutOfStockStatusEnum.Backorder)
            if (isBackorder) {
              const stockStatus = t('productBackorder', 'Backorder')
              return `${option} (${stockStatus})`
            }
            return option
          }

          return option
        }}
        getOptionDisabled={option => {
          if (option === INITIAL_VAL) {
            return true
          }
          if (currentProps.length) {
            const props = currentProps.find(props_ => props_.find(prop_ => prop_.valueName === option))

            if (props?.length) {
              const prop = props[0]
              return prop.stockStatus === OutOfStockStatusEnum.Out
            }
          }

          if (allProps.length) {
            // all variants have the stock status "out"
            return allProps
              .filter(props_ => props_.find(prop_ => prop_.valueName === option))
              .flat()
              .every(prop_ => prop_.stockStatus === OutOfStockStatusEnum.Out)
          }

          return false
        }}
        disableListWrap
        PopperComponent={StyledPopper}
        ListboxComponent={ListboxComponent}
        isOptionEqualToValue={(option, value) => option === value}
        PaperComponent={params => <Paper {...params} elevation={4} />}
        readOnly={options.length <= 1}
        forcePopupIcon={options.length > 1}
        renderInput={params => (
          <TextField
            {...params}
            name={label}
            variant='outlined'
            color='secondary'
            error={isInitial}
            margin='none'
            fullWidth
            inputProps={{
              ...params.inputProps,
              readOnly: options.length < 10,
              autoComplete: 'new-password',
              className: 'browser-default data-hj-allow ' + params.inputProps.className,
            }}
          />
        )}
      />
    </div>
  )
}
export default React.memo(VariantPickerWidget)
